import * as CL from '@design-system/component-library';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { dsClass } from '../../../../common/constants/dsClasses.js';
import { paths } from '../../../../common/constants/pathVariables.js';
import { useSearchParams } from '../../../../common/hooks/useSearchParams.js';
import { returnToCheckoutMsg, somethingWentWrongMsg, t } from '../../../../common/i18n';
import { useFinalizeCardPayment } from '../../hooks/useFinalizeCardPayment.js';
import { getFormDataFromSession } from '../../utils/cardPaymentUtils.js';

import './PlaceOrderCardPayment.scss';

type PaymentStatus = 'loading' | 'success' | 'cancelled' | 'failed' | 'session-expired';

/**
 * PaymentCancelled - UI shown when user cancels payment at Nets
 */
const PaymentCancelled = ({ onRedirectToCheckout }: { onRedirectToCheckout: () => void }) => (
  <div className="of-place-order-card-payment__content">
    <div>
      <CL.Icon icon="information" size="xl" color="red-600" />
      <h2 className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_2}`}>{t.BF36('Payment cancelled')}</h2>
      <h3 className={`${dsClass.H3_BOOK} ${dsClass.MARGIN_TOP_0} ${dsClass.MARGIN_BOTTOM_4}`}>
        {t.OFAN('You can continue shopping.')}
      </h3>
      <CL.Button size="l" onClick={onRedirectToCheckout}>
        {t.PLAX(returnToCheckoutMsg)}
      </CL.Button>
    </div>
  </div>
);

/**
 * PaymentFailed - UI shown when payment verification fails or finalize API returns error
 */
const PaymentFailed = ({ onRedirectToCheckout }: { onRedirectToCheckout: () => void }) => (
  <div className="of-place-order-card-payment__content">
    <div>
      <CL.Icon icon="information" size="xl" color="red-600" />
      <h2 className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_2}`}>{t.U7HJ('Payment verification failed')}</h2>
      <h3 className={`${dsClass.H3_BOOK} ${dsClass.MARGIN_TOP_0} ${dsClass.MARGIN_BOTTOM_4}`}>
        {t.UFR3('Unfortunately the payment verification failed. Try again or use some other payment method.')}
      </h3>
      <CL.Button size="l" onClick={onRedirectToCheckout}>
        {t.PLAX(returnToCheckoutMsg)}
      </CL.Button>
    </div>
  </div>
);

/**
 * SessionExpired - UI shown when session data is missing (session expired)
 */
const SessionExpired = ({ onRedirectToCheckout }: { onRedirectToCheckout: () => void }) => (
  <div className="of-place-order-card-payment__content">
    <div>
      <CL.Icon icon="information" size="xl" color="red-600" />
      <h2 className={`${dsClass.MARGIN_TOP_4} ${dsClass.MARGIN_BOTTOM_2}`}>{t.V0VJ('Oops, something went wrong')}</h2>
      <h3 className={`${dsClass.H3_BOOK} ${dsClass.MARGIN_TOP_0} ${dsClass.MARGIN_BOTTOM_4}`}>
        {t.JDTO(somethingWentWrongMsg)}
      </h3>
      <CL.Button size="l" onClick={onRedirectToCheckout}>
        {t.PLAX(returnToCheckoutMsg)}
      </CL.Button>
    </div>
  </div>
);

/**
 * PlaceOrderCardPayment - Handles return from Nets payment page
 *
 * This component is shown when user returns from Nets after card payment.
 * It parses query parameters (transactionId, responseCode) and retrieves orderId from session storage.
 * - If responseCode === 'OK': calls finalize API, then navigates to thank you page
 * - If responseCode === 'Cancel': shows payment cancelled UI
 * - Otherwise: shows payment failed UI
 */
export const PlaceOrderCardPayment = () => {
  const navigate = useNavigate();
  // transactionId and responseCode from URL query params
  const { transactionId, responseCode } = useSearchParams<{
    transactionId: string;
    responseCode: string;
  }>();

  // orderId from session storage (saved during initialize before Nets redirect)
  const sessionData = getFormDataFromSession();
  const orderId = sessionData?.orderId;

  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('loading');
  const { finalizeStatus, finalizeCardPayment } = useFinalizeCardPayment();

  // Prevent duplicate finalize calls (React StrictMode, re-renders, etc.)
  const hasInitiatedFinalize = useRef(false);

  const handleRedirectToCheckout = () => {
    navigate(paths.PLACE_ORDER);
  };

  // Handle finalize API status changes
  useEffect(() => {
    if (finalizeStatus === 'error') {
      setPaymentStatus('failed');
    }
    // Note: 'success' status triggers navigation in the hook, so we don't need to handle it here
  }, [finalizeStatus]);

  useEffect(() => {
    // Edge case: Missing session data (session expired)
    if (!orderId) {
      setPaymentStatus('session-expired');
      return;
    }

    // Edge case: Missing transactionId from URL
    if (!transactionId) {
      setPaymentStatus('failed');
      return;
    }

    if (responseCode === 'OK') {
      // Prevent duplicate finalize calls
      if (hasInitiatedFinalize.current) {
        return;
      }
      hasInitiatedFinalize.current = true;

      // Call finalize API
      finalizeCardPayment({ orderId, transactionId });
    } else if (responseCode === 'Cancel') {
      setPaymentStatus('cancelled');
    } else {
      // Unknown or error response code (includes empty responseCode)
      setPaymentStatus('failed');
    }
  }, [orderId, transactionId, responseCode, finalizeCardPayment]);

  // Loading state - shown while processing
  if (paymentStatus === 'loading') {
    return (
      <div className="of-place-order-card-payment__content">
        <div>
          <CL.LoadingSpinner className={dsClass.MARGIN_BOTTOM_4} size="l" />
          <h2 className={`${dsClass.MARGIN_TOP_0} ${dsClass.MARGIN_BOTTOM_2}`}>
            {t.AL0L('The order is being processed')}
          </h2>
          <h3 className={`${dsClass.H3_BOOK} ${dsClass.MARGIN_TOP_0} ${dsClass.MARGIN_BOTTOM_1}`}>
            {t.C9XG('Wait a moment...')}
          </h3>
        </div>
      </div>
    );
  }

  // Cancelled state - user cancelled payment at Nets
  if (paymentStatus === 'cancelled') {
    return <PaymentCancelled onRedirectToCheckout={handleRedirectToCheckout} />;
  }

  // Session expired state - session data is missing
  if (paymentStatus === 'session-expired') {
    return <SessionExpired onRedirectToCheckout={handleRedirectToCheckout} />;
  }

  // Failed state - payment verification failed or finalize API error
  return <PaymentFailed onRedirectToCheckout={handleRedirectToCheckout} />;
};
